import type { InstantCallResult } from '@schwarz/contract';
import { dialRefusalGroupPhrases, dialRefusalMessages } from '@schwarz/contract';

/** A time, in the operator's own locale, for the reopening hint. */
function shortTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  return at.toLocaleString(undefined, {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** A contact's name, or a stand-in when it was deleted mid-request. */
function nameOf(name: string | null): string {
  return name ?? 'A contact';
}

/**
 * What to tell the operator after an instant call.
 *
 * The rule throughout: name the contact and say the actual reason. "Some calls
 * could not be placed" is the message that makes somebody click again for no
 * reason — and for a do-not-call contact, clicking again will never work.
 *
 * Single-contact and batch read differently because they are different
 * questions. One contact asks "did that work?"; a batch asks "which ones do I
 * need to deal with?"
 */
export function callMessage(result: InstantCallResult): {
  kind: 'success' | 'error';
  message: string;
} {
  const { placed, attempts } = result;
  const refused = attempts.filter((a) => !a.placed);
  const kind = placed > 0 ? 'success' : 'error';

  // One contact: the reason is the whole message.
  if (attempts.length === 1) {
    const only = attempts[0];
    if (!only) return { kind, message: 'Nothing to call' };
    if (only.placed) return { kind: 'success', message: `Calling ${nameOf(only.name)}` };
    return { kind: 'error', message: `${nameOf(only.name)} ${reasonFor(only)}` };
  }

  const started = placed === 1 ? '1 call started' : `${placed} calls started`;
  if (refused.length === 0) return { kind, message: started };

  // A batch with one problem can still name it, which is usually the case that
  // matters — the operator selected a page and one contact was suppressed.
  if (refused.length === 1) {
    const one = refused[0];
    if (!one) return { kind, message: started };
    const tail = `${nameOf(one.name)} ${reasonFor(one)}`;
    return { kind, message: placed === 0 ? tail : `${started} — ${tail}` };
  }

  // Several problems: group by reason rather than listing every name, which
  // would overflow a toast on a full page of contacts.
  const grouped = groupReasons(refused);
  return { kind, message: placed === 0 ? grouped : `${started} — ${grouped}` };
}

/** The reason clause for one attempt, with the reopening time where we have it. */
function reasonFor(attempt: InstantCallResult['attempts'][number]): string {
  const reason = attempt.reason;
  if (!reason) return 'could not be called';

  const base = dialRefusalMessages[reason];
  if (reason === 'OUTSIDE_HOURS' && attempt.nextOpenAt) {
    const when = shortTime(attempt.nextOpenAt);
    if (when) return `${base} — calling resumes ${when}`;
  }
  return base;
}

/** "2 on the do-not-call list, 1 outside calling hours". */
function groupReasons(refused: readonly InstantCallResult['attempts'][number][]): string {
  const counts = new Map<string, number>();
  for (const attempt of refused) {
    const phrase = attempt.reason ? dialRefusalGroupPhrases[attempt.reason] : 'not called';
    counts.set(phrase, (counts.get(phrase) ?? 0) + 1);
  }
  return [...counts].map(([phrase, n]) => `${n} ${phrase}`).join(', ');
}
